import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MailCheck, ChevronDown, ChevronRight } from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
import { api } from '../lib/api';
import { ManualCreateForm } from '../components/ManualCreateForm';
import { Pagination } from '../components/Pagination';
import { EmptyState } from '../components/ui/empty-state';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { BulkActionBar } from '../components/BulkActionBar';
import { OperationResultStack, type OperationResult } from '../components/OperationResultStack';
import { ConfirmDialog, type ConfirmDialogProps } from '../components/ConfirmDialog';
import { useRowSelection } from '../hooks/useRowSelection';
import { formatDateTime } from '../lib/format';

// Map server-side parser_error code → human explanation in Vietnamese.
const ERROR_HINTS: Record<string, string> = {
  unknown_bank: 'Worker không nhận diện được ngân hàng từ địa chỉ from_addr.',
  no_parser: 'Bank đã được nhận diện nhưng chưa có parser implementation.',
  unknown_card: 'Số thẻ (last 4 digits) chưa được đăng ký trong bảng cards.',
  missing_required_field:
    'Body thiếu một hoặc nhiều label bắt buộc (Thẻ, Số tiền, Ngày, Sử dụng tại, Tình trạng).',
  card_format: 'Dòng "Thẻ" có định dạng không nhận diện được (mong đợi "Visa 466238...8653").',
  amount_format: 'Dòng "Số tiền" không khớp pattern "<số> <currency 3 chữ cái>".',
  amount_value: 'Số tiền không phải số dương hợp lệ.',
  date_format: 'Dòng "Ngày, giờ" không khớp pattern "DD-MM-YYYY HH:MM:SS".',
  fx_unavailable: 'Currency không phải VND nhưng FX API không trả tỷ giá.',
  hmac_signature_mismatch:
    'GAS gửi sai chữ ký HMAC — kiểm tra INGEST_HMAC_SECRET hai bên có khớp không.',
};

function explainError(code: string): string {
  const colonIdx = code.indexOf(':');
  const baseCode = colonIdx >= 0 ? code.slice(0, colonIdx) : code;
  return ERROR_HINTS[baseCode] ?? '';
}

export function ParseFailedPage() {
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const page = Math.max(1, parseInt(params.get('page') ?? '1', 10));
  const { data, isLoading } = useQuery({
    queryKey: ['raw-failed', page],
    queryFn: () => api.listFailedRawEmails(page),
  });
  const rows = data?.rows ?? [];
  const totalMatching = data?.total_count ?? rows.length;
  const resetKey = `${page}`;
  const { selectedIds, allOnPage, toggle, toggleAllOnPage, clear } = useRowSelection(
    rows,
    resetKey,
  );
  const [manualForId, setManualForId] = useState<number | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [opResults, setOpResults] = useState<OperationResult[]>([]);

  function pushResult(res: OperationResult) {
    setOpResults((p) => [res, ...p.filter((r) => r.key !== res.key)].slice(0, 5));
  }

  const retry = useMutation({
    mutationFn: (id: number) => api.retryRawEmail(id).then((result) => ({ id, result })),
    onSuccess: ({ id, result }) => {
      pushResult(
        result.processed
          ? {
              key: `retry-${id}`,
              kind: 'success',
              title: `Retry #${id} thành công`,
              detail: `Đã tạo transaction #${result.transaction_id}${
                typeof result.alerts_count === 'number' && result.alerts_count > 0
                  ? ` · ${result.alerts_count} alert`
                  : ''
              }.`,
            }
          : {
              key: `retry-${id}`,
              kind: 'failure',
              title: `Retry #${id} đã chạy nhưng vẫn fail`,
              detail: result.failure_reason ?? 'unknown',
            },
      );
      qc.invalidateQueries({ queryKey: ['raw-failed'] });
    },
  });
  const skip = useMutation({
    mutationFn: (id: number) => api.skipRawEmail(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['raw-failed'] }),
  });
  const manualCreate = useMutation({
    mutationFn: ({ id, body }: { id: number; body: never }) => api.manualCreate(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['raw-failed'] });
      setManualForId(null);
    },
  });
  const bulkSkip = useMutation({
    mutationFn: (ids: number[]) => api.bulkSkipRawEmails(ids),
    onSuccess: (res) => {
      pushResult({
        key: `bulk-skip-${Date.now()}`,
        kind: 'success',
        title: `Đã skip ${res.affected} email`,
      });
      clear();
      setConfirm(null);
      qc.invalidateQueries({ queryKey: ['raw-failed'] });
    },
    onError: (e) =>
      pushResult({
        key: `bulk-skip-err-${Date.now()}`,
        kind: 'failure',
        title: 'Bulk skip fail',
        detail: String(e).slice(0, 200),
      }),
  });
  const bulkRetry = useMutation({
    mutationFn: (ids: number[]) => api.bulkRetryRawEmails(ids),
    onSuccess: (res) => {
      pushResult({
        key: `bulk-retry-${Date.now()}`,
        kind: 'success',
        title: `Đã queue retry ${res.queued} email`,
        detail: 'Worker đang xử lý nền — refresh sau ~1 phút.',
      });
      clear();
      setConfirm(null);
      qc.invalidateQueries({ queryKey: ['raw-failed'] });
    },
    onError: (e) =>
      pushResult({
        key: `bulk-retry-err-${Date.now()}`,
        kind: 'failure',
        title: 'Bulk retry fail',
        detail: String(e).slice(0, 200),
      }),
  });

  const [confirm, setConfirm] = useState<Omit<ConfirmDialogProps, 'open' | 'onCancel'> | null>(
    null,
  );

  return (
    <div className="space-y-4 pb-24">
      <PageHeader title="Parse Failed Queue" />
      <OperationResultStack
        items={opResults}
        onDismiss={(k) => setOpResults((p) => p.filter((r) => r.key !== k))}
      />
      {isLoading && <Skeleton className="h-40 w-full" />}
      {data && rows.length === 0 && (
        <EmptyState
          icon={MailCheck}
          title="No failed emails"
          description="Không có email parse fail nào. 🎉"
        />
      )}
      {data && rows.length > 0 && (
        <>
          <div className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm">
            <input
              type="checkbox"
              checked={allOnPage}
              onChange={toggleAllOnPage}
              className="h-5 w-5 cursor-pointer accent-primary"
              aria-label="Chọn tất cả trang"
            />
            <span className="text-muted-foreground">Chọn tất cả ({rows.length} dòng)</span>
          </div>
          <div className="space-y-3">
            {rows.map((r) => {
              const isExpanded = expandedId === r.id;
              const isSelected = selectedIds.has(r.id);
              const hint = explainError(r.parser_error);
              return (
                <div
                  key={r.id}
                  className={`rounded-lg border bg-card transition-colors ${
                    isExpanded
                      ? 'border-primary/40'
                      : isSelected
                        ? 'border-primary/30'
                        : 'border-border'
                  }`}
                >
                  <div className="flex items-start gap-3 p-4">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggle(r.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="mt-0.5 h-5 w-5 cursor-pointer accent-primary"
                      aria-label={`Chọn email #${r.id}`}
                    />
                    <button
                      type="button"
                      onClick={() => setExpandedId(isExpanded ? null : r.id)}
                      className="flex-1 text-left focus-visible:outline-none"
                    >
                      <div className="mb-2 flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">
                          #{r.id} · {formatDateTime(r.received_at)}
                        </span>
                        <Badge variant="destructive">{r.parser_error}</Badge>
                      </div>
                      <div className="text-sm">
                        <span className="text-muted-foreground">{r.from_addr}</span>
                        <span className="text-muted-foreground/70"> — </span>
                        <span className="text-muted-foreground">{r.subject}</span>
                      </div>
                      <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground/70">
                        {isExpanded ? (
                          <ChevronDown size={12} strokeWidth={1.5} />
                        ) : (
                          <ChevronRight size={12} strokeWidth={1.5} />
                        )}
                        {isExpanded ? 'Click để thu gọn' : 'Click để xem chi tiết'}
                      </div>
                    </button>
                  </div>

                  {isExpanded && (
                    <div className="border-t border-border p-4 text-sm">
                      {hint && (
                        <div className="mb-3 rounded border border-warning/30 bg-warning/10 p-3 text-warning">
                          <div className="font-medium">Giải thích lỗi</div>
                          <div className="mt-1 text-muted-foreground">{hint}</div>
                        </div>
                      )}

                      <dl className="mb-3 grid grid-cols-[120px_1fr] gap-y-1 text-xs">
                        <dt className="text-muted-foreground">ID</dt>
                        <dd className="text-foreground">{r.id}</dd>
                        <dt className="text-muted-foreground">Gmail msg ID</dt>
                        <dd className="font-mono text-foreground">{r.gmail_message_id}</dd>
                        <dt className="text-muted-foreground">Received at</dt>
                        <dd className="text-foreground">{formatDateTime(r.received_at)}</dd>
                        <dt className="text-muted-foreground">From</dt>
                        <dd className="text-foreground">{r.from_addr}</dd>
                        <dt className="text-muted-foreground">Subject</dt>
                        <dd className="text-foreground">{r.subject}</dd>
                        <dt className="text-muted-foreground">Parser error</dt>
                        <dd className="font-mono text-destructive">{r.parser_error}</dd>
                        <dt className="text-muted-foreground">Status</dt>
                        <dd className="text-foreground">{r.parsed_status}</dd>
                      </dl>

                      <div className="mb-3">
                        <div className="mb-1 text-xs font-medium text-muted-foreground">
                          Email body ({r.body_text.length} chars)
                        </div>
                        <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded bg-background p-3 text-xs text-muted-foreground">
                          {r.body_text || '(empty body)'}
                        </pre>
                      </div>

                      <div className="flex gap-2 text-sm">
                        <Button
                          variant="secondary"
                          onClick={() => retry.mutate(r.id)}
                          disabled={retry.isPending && retry.variables === r.id}
                        >
                          {retry.isPending && retry.variables === r.id ? 'Đang retry…' : 'Retry'}
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={() => setManualForId(manualForId === r.id ? null : r.id)}
                        >
                          {manualForId === r.id ? 'Hủy form' : 'Manual create'}
                        </Button>
                        <Button
                          variant="destructive"
                          onClick={() => skip.mutate(r.id)}
                          disabled={skip.isPending}
                        >
                          Skip
                        </Button>
                      </div>

                      {manualForId === r.id && (
                        <div className="mt-3">
                          <ManualCreateForm
                            onSubmit={(body) =>
                              manualCreate.mutate({ id: r.id, body: body as never })
                            }
                            onCancel={() => setManualForId(null)}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <Pagination page={page} totalPages={data.total_pages} />
        </>
      )}

      <BulkActionBar
        selectedCount={selectedIds.size}
        totalMatching={totalMatching}
        isAllOnPageSelected={allOnPage}
        filterMode={false}
        onEscalateToFilter={() => {}}
        onClear={clear}
      >
        <Button
          variant="default"
          onClick={() => {
            const ids = [...selectedIds];
            const capped = ids.length > 50;
            setConfirm({
              title: `Retry ${ids.length} email?`,
              description: capped
                ? `Giới hạn ${50} email mỗi lần. Hãy thu hẹp lựa chọn.`
                : `Worker sẽ chạy lại LLM parse cho ${ids.length} email. Quá trình chạy nền, kết quả cập nhật sau ~1 phút.`,
              confirmLabel: 'Retry',
              tone: 'primary',
              pending: bulkRetry.isPending,
              onConfirm: () => {
                if (capped) {
                  setConfirm(null);
                  return;
                }
                bulkRetry.mutate(ids);
              },
            });
          }}
          disabled={bulkRetry.isPending || bulkSkip.isPending}
        >
          {bulkRetry.isPending ? 'Đang…' : 'Retry'}
        </Button>
        <Button
          variant="destructive"
          onClick={() => {
            const ids = [...selectedIds];
            setConfirm({
              title: `Skip ${ids.length} email?`,
              description: `${ids.length} email sẽ chuyển sang trạng thái "skipped" và biến mất khỏi queue. Không thể undo.`,
              confirmLabel: 'Skip',
              tone: 'destructive',
              pending: bulkSkip.isPending,
              onConfirm: () => bulkSkip.mutate(ids),
            });
          }}
          disabled={bulkSkip.isPending || bulkRetry.isPending}
        >
          {bulkSkip.isPending ? 'Đang…' : 'Skip'}
        </Button>
      </BulkActionBar>

      <ConfirmDialog
        open={confirm != null}
        title={confirm?.title ?? ''}
        description={confirm?.description ?? ''}
        confirmLabel={confirm?.confirmLabel ?? 'OK'}
        tone={confirm?.tone}
        pending={confirm?.pending}
        onConfirm={() => confirm?.onConfirm()}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}
